import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * A CRM mutation the agent performed, pushed by the backend over the chat stream as a
 * `data_changed` signal (see ChatService). `resource` matches the `app://` resource hosts used for
 * navigation (building, customer, mortgage, valuation, application, rate); `id` is null for a create,
 * where only the resource's list for the customer changed. A lender rate belongs to no customer and is
 * keyed `lenderNumber_rateNumber`, like its `app://rate/…` link.
 */
export interface DataChangedEvent {
  resource: string;
  id: string | null;
  customerId: string | null;
}

/**
 * App-wide bus for `data_changed` signals emitted by the agent when it writes CRM data. Detail/list
 * views can inject this service and subscribe to {@link changes$} to re-fetch when the entity they
 * display changes, without depending on ChatService.
 *
 * Example:
 *   this.dataChange.changes$
 *     .pipe(filter(e => e.resource === 'mortgage' && e.id === this.mortgageId))
 *     .subscribe(() => this.reload());
 */
@Injectable({ providedIn: 'root' })
export class DataChangeService {
  private readonly changes = new Subject<DataChangedEvent>();

  /** Stream of data-changed signals. */
  readonly changes$: Observable<DataChangedEvent> = this.changes.asObservable();

  /** Publishes a data-changed signal to all subscribers. */
  notify(event: DataChangedEvent): void {
    this.changes.next(event);
  }
}
